import {
  GraphCard,
  zonedToUTC,
  zonedToUTCAdd1Day,
  useTranslation
} from '@fareye/common'
import { Flex, Graph, RenderControl, InfoIcon } from '@fareye/ui'
import {
  AxisFormatter,
  cancelToken,
  isEmpty,
  responseParser,
  post,
  groupBy
} from '@fareye/utils'
import React from 'react'
import { inTransitConstant } from '../../../config/constants'
import queryString from 'query-string'
import { getYKeys } from '../../../utility/funcs'
import { IRouteDetails } from '../../../utility/types'
import { PieGraph } from '../components/Pie'

import MultiModalTransporterCard from '../components/MultiModalTransporterCard'
import { IApiFilter } from '@fareye/types/lib/types'

const MultiModalRouteDetails = ({
  match,
  config,
  endDate,
  location,
  startDate,
  globalSearch
}: IRouteDetails) => {
  const { t } = useTranslation()
  const { route }: any = match.params
  const { transportMode = 'road', stops = 0 } = queryString.parse(
    location.search
  )
  const source = cancelToken().source()

  const {
    config: { apiConfig, metricsCount, graphsToShow = {} },
    styling: { style, className, variant }
  } = config
  const {
    tripStatusGraph,
    exceptionsGraph,
    epodRatingsGraph,
    transporterPerformanceGraph
  } = graphsToShow
  const [apiResponse, setApiResponse] = React.useState<any>({
    data: [],
    isLoading: false,
    isError: false,
    isSuccess: false,
    message: ''
  })
  const [exceptionsData, setExceptionsData] = React.useState<any>([])
  const [epodRatingsData, setEpodRatingsData] = React.useState<any>([])
  const getRoute = (route: string) => {
    const [origin, destination] = route.split('-to-')
    return origin !== 'null'
      ? destination !== 'null'
        ? `${origin}-${destination}`
        : origin
      : destination
  }
  function generateFilterDataFromGlobalSearch(globalSearch: any[]) {
    if (isEmpty(globalSearch)) {
      return []
    } else {
      const groupedSearch = groupBy(globalSearch, ele => ele.key)
      return Object.entries(groupedSearch).map(([key, value]) => {
        return [
          {
            filterKey: key,
            filterValue: value.reduce((res = [], obj) => {
              res.push(obj.value)
              return res
            }, []),
            condition: 'include',
            filterType: 'eq'
          }
        ]
      })
    }
  }
  function generateFilterObjectForDateRange(startDate: any, endDate: any) {
    const startFilter = [
      {
        filterKey: 'start_date',
        filterValue: startDate ? zonedToUTC(startDate) : new Date(),
        condition: 'include',
        filterType: 'gte'
      }
    ]
    const endFilter = [
      {
        filterKey: 'start_date',
        filterValue: endDate ? zonedToUTCAdd1Day(endDate) : new Date(),
        condition: 'include',
        filterType: 'lt'
      }
    ]
    const locationFilter = [
      {
        filterKey: 'parcel_route_name',
        filterValue: getRoute(route),
        condition: 'include',
        filterType: 'eq'
      }
    ]
    const deliveredFilter = [
      {
        filterKey: 'milestone_carrier_status',
        filterValue: 'DELIVERED',
        condition: 'include',
        filterType: 'eq'
      }
    ]
    // ...locationFilter,
    return [startFilter, endFilter, locationFilter, deliveredFilter]
  }
  async function fetchData() {
    try {
      const { apiURL, queryParams = {} } = apiConfig as IApiFilter
      setApiResponse({ ...apiResponse, isLoading: true })
      let filter: any = generateFilterDataFromGlobalSearch(globalSearch)
      const dateFilter = generateFilterObjectForDateRange(startDate, endDate)
      filter = [...filter, ...dateFilter]
      const {
        data: {
          vehicleMetrics,
          transporterPerformanceCard,
          alertsTriggered,
          epodRatings
        },
        message,
        error
      } = responseParser(
        await post(
          `${apiURL}`,
          {
            filter,
            perPage: 10000,
            page: 0,
            ...queryParams
            // ...generateSortingObj()
          },
          source.token
        )
      )
      if (error) {
        setApiResponse({
          ...apiResponse,
          isError: true,
          data: [],
          isSuccess: true,
          isLoading: false,
          message
        })
      } else {
        setApiResponse({
          ...apiResponse,
          isError: false,
          data: {
            vehicleMetrics,
            alertsTriggered,
            epodRatings,
            transporterPerformanceCard
          },
          isSuccess: true,
          isLoading: false,
          message:
            apiConfig &&
            apiConfig.apiDataState &&
            apiConfig.apiDataState.noDataMessage
        })
      }
    } catch (err:any) {
      const { message, error } = responseParser(err.response)
      setApiResponse({
        ...apiResponse,
        isSuccess: false,
        isError: true,
        isLoading: false,
        message
      })
      console.error('App Error:', error)
    }
  }
  React.useEffect(() => {
    fetchData()
    return () => {
      source.cancel('Unsubscribe Before Unmount')
    }
    // eslint-disable-line
  }, [globalSearch, startDate, endDate])

  const {
    data: {
      vehicleMetrics = {},
      alertsTriggered = {},
      epodRatings = {},
      transporterPerformanceCard = {}
    }
  } = apiResponse
  const metrics: any = {}
  vehicleMetrics &&
    Object.keys(vehicleMetrics).forEach((item: string) => {
      metrics[item] = vehicleMetrics[item].count
    })

  React.useEffect(() => {
    const exceptions: Array<any> = []
    Object.keys(alertsTriggered).forEach((item: string) => {
      exceptions.push({ label: AxisFormatter(item), ...alertsTriggered[item] })
    })
    setExceptionsData(exceptions.slice(0, 20))

    const ratings: Array<any> = []
    Object.keys(epodRatings).forEach((item: string) => {
      ratings.push({ label: AxisFormatter(item), ...epodRatings[item] })
    })
    setEpodRatingsData(ratings.slice(0, 20))
  }, [apiResponse.data])

  const tripStatusHeading = (
    <>
      {t('Performance')}
      <InfoIcon
        style={{ marginLeft: 5 }}
        title={t(`Performance Carrier Wise`)}
      />
    </>
  )

  const exceptionHeading = (
    <>
      {t(inTransitConstant.alertsLabel)}
      <InfoIcon
        style={{ marginLeft: 5 }}
        title={t(`Alerts Wise Distribution`)}
      />
    </>
  )

  const epodRatingsHeading = (
    <>
      {t('EPOD Ratings')}
      <InfoIcon
        style={{ marginLeft: 5 }}
        title={t(`Ratings Wise Distribution`)}
      />
    </>
  )

  return (
    <RenderControl state={apiResponse} variant={'m'}>
      <Flex
        p={3}
        style={{
          width: 'calc(100% - 32px)',
          overflow: 'auto hidden',
          ...style
        }}
        className={className}
      >
        <Flex style={{ padding: '2px 4px', overflow: 'auto hidden' }}>
          {tripStatusGraph?.show && !isEmpty(metrics) && (
            <GraphCard
              heading={tripStatusHeading}
              data-config-id="tripStatusGraph"
            >
              <PieGraph
                data={metrics}
                metric={metricsCount}
                sumOfTrips={Object.values(metrics).reduce(
                  (acc: number, num: any) => acc + num,
                  0
                )}
              />
            </GraphCard>
          )}
          {exceptionsGraph?.show && !isEmpty(exceptionsData) && (
            <GraphCard graphId="exceptions" heading={exceptionHeading}>
              {/* {!isEmpty(exceptionsData) ? ( */}
              <div>
                <Graph
                  data={exceptionsData}
                  type="bar"
                  config={{
                    colors: {},
                    legendsList: ['']
                  }}
                  ratio={1.3}
                  selector="exceptions"
                  XLabel={t('Date')}
                  YLabel={t('Count of Alerts')}
                  XLabelKey={'label'}
                  YLabelKey={getYKeys(exceptionsData)}
                  stacked="tower"
                />
              </div>
              {/* ) : (
                  <Span>{inTransitConstant.noAlertsLabel}</Span>
                )} */}
            </GraphCard>
          )}
          {epodRatingsGraph?.show && !isEmpty(epodRatingsData) && (
            <GraphCard graphId="epod-ratings" heading={epodRatingsHeading}>
              {/* {!isEmpty(exceptionsData) ? ( */}
              <div>
                <Graph
                  data={epodRatingsData}
                  type="bar"
                  config={{
                    colors: {},
                    legendsList: ['']
                  }}
                  ratio={1.3}
                  selector="epod-ratings"
                  XLabel={t('Carriers')}
                  YLabel={t('Count')}
                  XLabelKey={'label'}
                  YLabelKey={getYKeys(epodRatingsData)}
                  stacked="tower"
                />
              </div>
              {/* ) : (
                  <Span>{inTransitConstant.noAlertsLabel}</Span>
                )} */}
            </GraphCard>
          )}

          {
            <MultiModalTransporterCard
              transporterData={transporterPerformanceCard}
              label="Carrier Performance"
            />
          }
        </Flex>
      </Flex>
    </RenderControl>
  )
}
export default MultiModalRouteDetails
